<template>
  <VToolbar
    class="ChartToolbar"
    dense
  >
    <!-- Выбор даты для графика -->
    <VBtnToggle
      class="date-btn-group"
      dense
      group
      :style="{
        border: `1px solid ${disabled ? OFF_COLOR : theme.primary}`,
      }"
    >
      <VBtn
        :disabled="disabled"
        :style="{ color: disabled ? OFF_COLOR : theme.primary }"
        @click="scrollLeft()"
      >
        <VIcon>mdi-chevron-left</VIcon>
      </VBtn>
      <DatePicker
        v-model="filters.date"
        :is-open.sync="ui.dateMenuIsOpen"
        :max="today()"
        :btn-style="{ color: disabled ? OFF_COLOR : theme.primary }"
        @update:is-open="$emit('update:dateMenuIsOpen', $event)"
      />
      <VBtn
        :disabled="disabled || filters.date === today()"
        :style="{ color: disabled ? OFF_COLOR : theme.primary }"
        @click="scrollRight()"
      >
        <VIcon>mdi-chevron-right</VIcon>
      </VBtn>
    </VBtnToggle>

    <!-- Выбор пользователей для графика -->
    <UserSelector
      v-model="filters.userIds"
      :disabled="disabled"
      outlined
    />

    <!-- Выбор группировки для графика -->
    <VBtnToggle
      :value="options.grouping === 'user' ? 0 : 1"
      mandatory
      dense
      color="primary"
      :style="{ border: `1px solid ${disabled ? OFF_COLOR : theme.primary}` }"
      @change="options.grouping = $event === 0 ? 'user' : 'category'"
    >
      <VTooltip bottom>
        <template #activator="{ on }">
          <VBtn
            :disabled="disabled"
            v-on="on"
          >
            <VIcon
              color="primary"
              v-text="'mdi-account-outline'"
            />
          </VBtn>
        </template>
        <span>Детализация по пользователям</span>
      </VTooltip>
      <VTooltip bottom>
        <template #activator="{ on }">
          <VBtn
            :disabled="disabled"
            v-on="on"
          >
            <VIcon
              color="primary"
              v-text="'mdi-tag-outline'"
            />
          </VBtn>
        </template>
        <span>Детализация по категориям событий</span>
      </VTooltip>
    </VBtnToggle>

    <!-- Выбор шкалы Y - пользователи или типы событий -->
    <VBtnToggle
      :value="options.typesAsCategories ? 1 : 0"
      mandatory
      dense
      color="primary"
      :style="{
        border: `1px solid ${extraOptionsDisabled ? OFF_COLOR : theme.primary}`,
      }"
      @change="options.typesAsCategories = $event === 1"
    >
      <VTooltip bottom>
        <template #activator="{ on }">
          <VBtn
            :disabled="extraOptionsDisabled"
            v-on="on"
          >
            <VIcon
              color="primary"
              v-text="'mdi-account-multiple-outline'"
            />
          </VBtn>
        </template>
        <span>Пользователи на шкале Y</span>
      </VTooltip>
      <VTooltip bottom>
        <template #activator="{ on }">
          <VBtn
            :disabled="extraOptionsDisabled"
            v-on="on"
          >
            <VIcon
              color="primary"
              v-text="'mdi-tag-multiple-outline'"
            />
          </VBtn>
        </template>
        <span>Типы событий на шкале Y</span>
      </VTooltip>
    </VBtnToggle>

    <!-- Выбор детального/обычного просмотра -->
    <VBtnToggle
      :value="options.wideView ? 1 : 0"
      mandatory
      dense
      color="primary"
      :style="{
        border: `1px solid ${wideViewDisabled ? OFF_COLOR : theme.primary}`,
      }"
      @change="options.wideView = $event === 1"
    >
      <VTooltip bottom>
        <template #activator="{ on }">
          <VBtn
            :disabled="wideViewDisabled"
            v-on="on"
          >
            <VIcon
              color="primary"
              v-text="'mdi-arrow-collapse-vertical'"
            />
          </VBtn>
        </template>
        <span>Все типы событий в одной строке</span>
      </VTooltip>
      <VTooltip bottom>
        <template #activator="{ on }">
          <VBtn
            :disabled="wideViewDisabled"
            v-on="on"
          >
            <VIcon
              color="primary"
              v-text="'mdi-arrow-expand-vertical'"
            />
          </VBtn>
        </template>
        <span>Каждый тип событий на отдельной строке</span>
      </VTooltip>
    </VBtnToggle>

    <!-- Конпка дозагрузки событий сверх лимита -->
    <VTooltip
      v-if="!disabled && loadedEvents && totalEvents && loadedEvents < totalEvents"
      bottom
    >
      <template #activator="{ on }">
        <VBtn
          icon
          class="load-more-btn animate"
          v-on="on"
          @click="$emit('click:get-more-events')"
        >
          <VIcon
            color="deep-orange darken-1"
            v-text="'mdi-cloud-alert'"
          />
        </VBtn>
      </template>
      <span v-if="company && company.isPremium">
        Показаны первые {{ loadedEvents }}/{{ totalEvents }} событий,
        нажмите для загрузки следующих {{ loadMore }}
      </span>
      <span v-else-if="company">
        На бесплатном тарифе доступно не более
        {{ company.eventLimit }} событий на пользователя
      </span>
    </VTooltip>
  </VToolbar>
</template>

<script>
import moment from 'moment'
import * as R from 'ramda'

import UserSelector from './UserSelector'
import DatePicker from './DatePicker'

const ISO_DATE = 'YYYY-MM-DD'
const toIsoDate = (...args) => moment(...args).format(ISO_DATE)

export default {
  name: 'ChartToolbar',
  components: {
    DatePicker,
    UserSelector,
  },
  props: {
    // Фильтр пользователей на тулбаре, для использования с модификатором sync
    userIds: {
      type: Array,
      default: () => [],
      validator: R.both(R.is(Array), R.all(R.is(Number))),
    },
    // Фильтр по дате на тулбаре, для использования с модификатором sync
    date: { // ISO date: e.g.: '2020-02-01'
      type: String,
      default: () => this.today(),
    },
    // Заблокировать все элементы панели
    disabled: { type: Boolean, default: false },
    // Для открытия селекта даты из родительского компонента,
    // при закрытии меню значение можно получить наверх модификатором sync
    dateMenuIsOpen: { type: Boolean, default: false },
    // Группировка по пользователю или категории (также sync)
    grouping: {
      type: String,
      default: 'user',
      validator: R.includes(R.__, ['user', 'category']),
    },
    // Переключение каждый тип событий на своей полоске
    wideView: {
      type: Boolean,
      default: false,
    },
    // Включить типы событий на шкале Y
    typesAsCategories: {
      type: Boolean,
      default: false,
    },
    // Сколько загружено событий прямо сейчас
    loadedEvents: { type: Number, default: null },
    // Сколько всего событий на сервере
    totalEvents: { type: Number, default: null },
    // Сколько загружаем еще по нажатию кнопки подгрузки
    loadMore: { type: Number, default: null },
  },
  data() {
    return {
      OFF_COLOR: 'rgb(185, 185, 190)',
      filters: { date: null, userIds: [] },
      options: { grouping: 'user', wideView: false, typesAsCategories: false },
      ui: { dateMenuIsOpen: false },
    }
  },
  computed: {
    theme() {
      return this.$vuetify.theme.isDark
        ? this.$vuetify.theme.themes.dark
        : this.$vuetify.theme.themes.light
    },
    company() { return this.$store.state.company.current },
    extraOptionsDisabled() {
      const { disabled, options } = this
      return disabled || options.grouping !== 'category'
    },
    wideViewDisabled() {
      const { extraOptionsDisabled, options } = this
      return extraOptionsDisabled || options.typesAsCategories
    },
  },
  watch: {
    userIds(userIds) {
      if (userIds) this.filters.userIds = userIds
    },
    'filters.userIds'(userIds, oldUserIds) {
      if (R.equals(userIds, oldUserIds)) return
      this.$emit('update:userIds', userIds)
    },
    date(date) {
      if (date) this.filters.date = date
    },
    'filters.date'(date, oldDate) {
      if (date === oldDate) return
      this.$emit('update:date', date)
    },
    dateMenuIsOpen(dateMenuIsOpen) {
      this.ui.dateMenuIsOpen = dateMenuIsOpen
    },
    'ui.dateMenuIsOpen'(dateMenuIsOpen, wasOpen) {
      if (dateMenuIsOpen === wasOpen) return
      this.$emit('update:dateMenuIsOpen', dateMenuIsOpen)
    },
    grouping(grouping) {
      this.options.grouping = grouping
    },
    'options.grouping'(grouping, oldGrouping) {
      if (grouping === 'user' && this.options.wideView) {
        this.options.wideView = false
      }
      if (grouping === oldGrouping) return
      this.$emit('update:grouping', grouping)
    },
    wideView(wideView) {
      this.options.wideView = wideView
    },
    'options.wideView'(wideView, prev) {
      if (wideView === prev) return
      this.$emit('update:wideView', wideView)
    },
    typesAsCategories(typesAsCategories) {
      this.options.typesAsCategories = typesAsCategories
    },
    'options.typesAsCategories'(typesAsCategories, prev) {
      if (typesAsCategories && this.options.wideView) {
        this.options.wideView = false
      }
      if (typesAsCategories === prev) return
      this.$emit('update:typesAsCategories', typesAsCategories)
    },
  },
  created() {
    this.initFilters()
    this.initOptions()
  },
  methods: {
    today() { return toIsoDate() },
    initFilters() {
      this.filters = {
        date: this.date || this.today(),
        userIds: this.userIds || [],
      }
    },
    initOptions() {
      const { options, grouping, wideView, typesAsCategories } = this
      options.grouping = grouping
      options.wideView = wideView
      options.typesAsCategories = typesAsCategories
    },
    scrollLeft() {
      const { filters } = this
      filters.date = toIsoDate(moment(filters.date).subtract(1, 'day'))
    },
    scrollRight() {
      const { filters } = this
      filters.date = toIsoDate(moment(filters.date).add(1, 'day'))
    },
  },
}
</script>

<style lang="sass" scoped>
  .v-btn-toggle
    height: 36px
    border-radius: 4px
    background-color: transparent !important
    & ::v-deep .v-btn
      margin: 0 !important
      border: none !important
      height: 34px !important
      background-color: transparent !important

    &.date-btn-group
      & ::v-deep .v-btn
        opacity: 1 !important
        &:before
          background-color: transparent !important

  .ChartToolbar
    height: auto !important
    & ::v-deep .v-toolbar__content
      height: auto !important
      flex-wrap: wrap
      padding: 2px 7px
      & > i
        display: none
      & > *
        margin: 4px 7px
        max-width: 100%

  .load-more-btn
    height: 34px !important
    width: 34px !important
    box-shadow: 0 0 0 0 rgba(244, 81, 30, 0)
    &.animate
      animation: pulse 2s infinite

  @keyframes pulse
    0%
      box-shadow: 0 0 0 0 rgba(244, 81, 30, 0.4)
    80%
      box-shadow: 0 0 0 12px rgba(244, 81, 30, 00)
    100%
      box-shadow: 0 0 0 0 rgba(244, 81, 30, 0)
</style>
